// Polyfills e compat de scroll do DOM pra WebView ANTIGO — Chr38/53 é o PISO do
// produto (LG webOS 2017 / Samsung Tizen 2017 = Chr38; LG 2019 = Chr53). Nesses
// engines `Element.prototype.scrollBy` e `Element.prototype.scrollTo` NÃO existem
// (só a partir do Chromium 61): a chamada explode com "e.scrollBy is not a
// function" e derruba a tela inteira pelo error boundary — foi o que matou 100%
// da TV ao vivo (LiveScreen) no device real do Fernando. E `scrollIntoView` até
// EXISTE, mas só aceita `boolean` — o objeto `{block, inline}` é coagido a
// `true` (ver `scrollIntoViewCompat` abaixo).
//
// POR QUE ESTE MÓDULO MORA EM `packages/ui` (e não em `apps/tv`, onde nasceu):
// o call site mais QUENTE do compat de scroll é o `Focusable` — que vive aqui e
// roda a CADA mudança de foco do D-pad. `packages/ui` não pode importar de
// `apps/tv` (dependência invertida), então o módulo desce pro pacote e o
// `apps/tv` passa a importar dele (`@app-iptv/ui/compat`) — uma cópia só, um
// gate só. Continua sendo código COMUM do app (nada de wrapper tv-*), entra em
// QUALQUER variante de build (moderna e legacy), e `installScrollPolyfills()`
// segue sendo chamado no boot pelo `main.tsx` do apps/tv.
//
// GENÉRICO por CAPABILITY, jamais por marca (nada de `if (webOS)`):
//   - feature-detect: só instala o fallback se o método NÃO existir. No Chr
//     moderno os nativos permanecem em uso — zero regressão no build web.
//   - o patch é no prototype, então protege qualquer chamador de
//     Element.scrollBy/scrollTo, inclusive libs bundladas (ex.: Norigin
//     spatial-nav).

/**
 * Suporte NATIVO a scroll com options object (Chr61+). Capturado no LOAD do
 * módulo, ANTES de `installScrollPolyfills()` substituir os métodos — senão
 * leríamos o próprio polyfill como "nativo". No Chromium, `Element.scrollBy`/
 * `scrollTo` e o options-object de `scrollIntoView` chegaram JUNTOS no Chr61,
 * então a presença do `scrollBy` nativo é sinal confiável de que o options de
 * `scrollIntoView` também existe (nosso alvo é 100% Chromium: webOS/Tizen/
 * Android WebView).
 */
const NATIVE_SCROLL_OPTIONS_SUPPORTED: boolean =
  typeof Element !== 'undefined' &&
  typeof Element.prototype.scrollTo === 'function' &&
  typeof Element.prototype.scrollBy === 'function'

// `undefined` explícito no 1º elemento pra casar com o overload nativo
// `scrollTo(options?: ScrollToOptions)` sob `exactOptionalPropertyTypes` (que
// permite `scrollTo(undefined)`), senão a atribuição ao prototype não é aceita.
type ScrollArgs = [options?: ScrollToOptions | undefined] | [x: number, y: number]

/** Normaliza {left, top} tanto de `scrollX(x, y)` quanto de `scrollX({left, top})`. */
function resolveDelta(args: ScrollArgs): { left: number | undefined; top: number | undefined } {
  if (typeof args[0] === 'number') {
    return { left: args[0], top: typeof args[1] === 'number' ? args[1] : undefined }
  }
  const opts = args[0]
  return { left: opts?.left, top: opts?.top }
}

/**
 * Instala `Element.prototype.scrollBy` / `scrollTo` se ausentes (Chr <61).
 * Idempotente e feature-detected: no-op quando os nativos existem, então é
 * seguro chamar no boot em qualquer engine. `behavior: 'smooth'` é ignorado no
 * fallback (scroll instantâneo) — aceitável: o alvo é NÃO CRASHAR; suavidade é
 * secundária no piso Chr38. `scrollTo` = posição absoluta; `scrollBy` = delta.
 */
export function installScrollPolyfills(): void {
  if (typeof Element === 'undefined') return
  const proto = Element.prototype

  if (typeof proto.scrollTo !== 'function') {
    proto.scrollTo = function scrollToPolyfill(this: Element, ...args: ScrollArgs): void {
      const { left, top } = resolveDelta(args)
      if (typeof left === 'number') this.scrollLeft = left
      if (typeof top === 'number') this.scrollTop = top
    }
  }

  if (typeof proto.scrollBy !== 'function') {
    proto.scrollBy = function scrollByPolyfill(this: Element, ...args: ScrollArgs): void {
      const { left, top } = resolveDelta(args)
      if (typeof left === 'number') this.scrollLeft += left
      if (typeof top === 'number') this.scrollTop += top
    }
  }
}

/* ------------------------------------------------------------------ *
 * scrollIntoView com "nearest" DE VERDADE no engine antigo
 * ------------------------------------------------------------------ */

/**
 * Menor deslocamento que vale a pena escrever. Abaixo de 1px o scroll é
 * imperceptível e, com arredondamento, viraria jitter (o foco "vibrando" a cada
 * navegação). Também evita escrita inútil em `scrollTop`/`scrollLeft`, que força
 * reflow.
 */
const MIN_SCROLL_PX = 1

/** Só o que precisamos medir de um elemento — estrutural de propósito, pra que
 *  a geometria seja testável com objetos falsos, sem DOM real. */
interface RectLike {
  getBoundingClientRect(): { top: number; bottom: number; left: number; right: number }
}

/** Um container de scroll: caixa (client box) + posição de scroll + conteúdo. */
interface ScrollBoxLike extends RectLike {
  clientTop: number
  clientLeft: number
  clientHeight: number
  clientWidth: number
  scrollTop: number
  scrollLeft: number
  scrollHeight: number
  scrollWidth: number
}

/**
 * Deslocamento (em px) a SOMAR no `scrollTop`/`scrollLeft` do container pra
 * satisfazer `mode` num eixo. Função PURA e agnóstica de eixo: no vertical
 * passe (top, bottom); no horizontal, (left, right). Todos os valores no MESMO
 * referencial (coordenadas de cliente/viewport).
 *
 * `nearest` é o ponto da parede: é o modo que o `Focusable` usa a cada foco do
 * D-pad, e a tradução ingênua pro engine antigo (`scrollIntoView(true)`) alinha
 * ao TOPO — ou seja, cada seta do controle jogava a lista/rail pro começo em vez
 * de rolar o mínimo. Aqui `nearest` é implementado como manda o CSSOM View
 * ("scroll an element into view", passo do alinhamento `nearest`):
 *
 *   - já cabe inteiro na caixa            → 0 (NÃO ROLA — o caso mais comum)
 *   - cobre a caixa inteira (maior que ela, sobrando dos dois lados) → 0
 *   - sobra só pro início                 → alinha o INÍCIO (rola o mínimo)
 *   - sobra só pro fim, e CABE na caixa   → alinha o FIM (rola o mínimo)
 *   - sobra pro fim e NÃO cabe na caixa   → alinha o INÍCIO (mostra o começo;
 *                                           alinhar o fim esconderia a cabeça
 *                                           do elemento)
 */
export function computeAxisScrollDelta(
  elStart: number,
  elEnd: number,
  boxStart: number,
  boxEnd: number,
  mode: ScrollLogicalPosition,
): number {
  if (mode === 'start') return elStart - boxStart
  if (mode === 'end') return elEnd - boxEnd
  if (mode === 'center') return (elStart + elEnd) / 2 - (boxStart + boxEnd) / 2

  // 'nearest'
  const elSize = elEnd - elStart
  const boxSize = boxEnd - boxStart

  // Sobra dos DOIS lados (elemento maior que a caixa e cobrindo-a) ou dentro dos
  // DOIS lados (já visível por inteiro): qualquer scroll só pioraria. Não mexe.
  if (
    (elStart < boxStart && elEnd > boxEnd) ||
    (elStart > boxStart && elEnd < boxEnd)
  ) {
    return 0
  }

  // Alinha o INÍCIO: (a) sobra pro início e o elemento cabe na caixa — o clássico
  // "voltar um item no rail"; (b) sobra pro fim mas o elemento NÃO cabe — mostrar
  // a cabeça é melhor que mostrar o rabo.
  if ((elStart <= boxStart && elSize <= boxSize) || (elEnd >= boxEnd && elSize >= boxSize)) {
    return elStart - boxStart
  }

  // Alinha o FIM: sobra pro fim e o elemento cabe na caixa — "avançar um item".
  if ((elEnd > boxEnd && elSize < boxSize) || (elStart < boxStart && elSize > boxSize)) {
    return elEnd - boxEnd
  }

  return 0
}

/** Caixa de scroll (client box) do container, em coordenadas de cliente. */
function scrollBoxOf(
  container: ScrollBoxLike,
  isRoot: boolean,
): { top: number; bottom: number; left: number; right: number } {
  if (isRoot) {
    // Root scroller (<html>): a caixa é a VIEWPORT, cuja origem em coordenadas
    // de cliente é sempre (0,0). Usar o `getBoundingClientRect()` do <html> aqui
    // seria um bug: esse rect é o do DOCUMENTO INTEIRO e ANDA conforme a página
    // rola (top vira negativo), o que envenenaria toda a conta.
    return { top: 0, left: 0, bottom: container.clientHeight, right: container.clientWidth }
  }
  const r = container.getBoundingClientRect()
  const top = r.top + container.clientTop
  const left = r.left + container.clientLeft
  return {
    top,
    left,
    bottom: top + container.clientHeight,
    right: left + container.clientWidth,
  }
}

/**
 * Rola UM container o mínimo necessário pra satisfazer `block`/`inline` pro
 * elemento. Escreve direto em `scrollTop`/`scrollLeft` — as duas únicas
 * propriedades de scroll que existem em QUALQUER engine (inclusive Chr38), e
 * que o browser já clampa sozinho no range válido.
 *
 * Cada eixo só é tocado se o container REALMENTE rola nele (`scrollHeight >
 * clientHeight`): num rail horizontal, mexer no `scrollTop` seria escrita morta
 * (e reflow à toa).
 *
 * Exportada pra teste: é aqui que mora a prova do "nearest de verdade" (já
 * visível → não rola; fora da vista → rola o mínimo, não pro topo), nos 2 eixos.
 */
export function scrollElementIntoScrollBox(
  container: ScrollBoxLike,
  el: RectLike,
  block: ScrollLogicalPosition,
  inline: ScrollLogicalPosition,
  isRoot: boolean,
): void {
  const elRect = el.getBoundingClientRect()
  const box = scrollBoxOf(container, isRoot)

  if (container.scrollHeight > container.clientHeight) {
    const dy = computeAxisScrollDelta(elRect.top, elRect.bottom, box.top, box.bottom, block)
    if (Math.abs(dy) >= MIN_SCROLL_PX) {
      container.scrollTop = Math.round(container.scrollTop + dy)
    }
  }

  if (container.scrollWidth > container.clientWidth) {
    const dx = computeAxisScrollDelta(elRect.left, elRect.right, box.left, box.right, inline)
    if (Math.abs(dx) >= MIN_SCROLL_PX) {
      container.scrollLeft = Math.round(container.scrollLeft + dx)
    }
  }
}

/** O container tem conteúdo transbordando em ALGUM eixo? (checagem barata, feita
 *  antes do `getComputedStyle`, que é caro). */
function overflows(node: Element): boolean {
  return node.scrollHeight > node.clientHeight || node.scrollWidth > node.clientWidth
}

/** `visible`/`clip` (e o vazio do jsdom) não formam caixa de scroll. Qualquer
 *  outro valor (`auto`, `scroll`, `hidden`) forma — `hidden` inclusive: é
 *  rolável por programa, só não pelo usuário (é o que o CSSOM manda). */
function canOverflow(value: string | undefined): boolean {
  return value !== undefined && value !== '' && value !== 'visible' && value !== 'clip'
}

/** `getComputedStyle` do documento do próprio nó (nunca o global — o app pode
 *  render em portal/iframe). Defensivo: ambiente sem view devolve null e o
 *  chamador decide. */
function computedStyleOf(node: Element): CSSStyleDeclaration | null {
  const view = node.ownerDocument?.defaultView
  if (!view || typeof view.getComputedStyle !== 'function') return null
  try {
    return view.getComputedStyle(node)
  } catch {
    return null
  }
}

function isScrollContainer(node: Element): boolean {
  if (!overflows(node)) return false
  const style = computedStyleOf(node)
  // Sem `getComputedStyle` disponível, o transbordo real já é sinal suficiente.
  if (!style) return true
  return canOverflow(style.overflowY) || canOverflow(style.overflowX)
}

/**
 * Envolve o scroller da PÁGINA num `ScrollBoxLike` cuja CAIXA é a VIEWPORT — e
 * cujo `scrollTop`/`scrollLeft` movem a viewport de verdade em qualquer engine.
 *
 * POR QUE (o bug medido no Chromium 53 real da webOS, rev 403380): mesmo em
 * standards mode (`document.compatMode === 'CSS1Compat'`), `scrollingElement` é o
 * **`<body>`**, e `body.clientHeight` NÃO é a viewport (787) e sim ~a altura do
 * CONTEÚDO (5983). O `scrollBoxOf(isRoot)` usava `container.clientHeight`, então a
 * "viewport" virava o documento inteiro: TODO card já caía "dentro da caixa",
 * `computeAxisScrollDelta` devolvia 0 e a PÁGINA NUNCA ROLAVA no ↓ do D-pad — o
 * foco descia pros trilhos de baixo mas a viewport ficava parada (foco fora da
 * tela) e, sem scroll, o IntersectionObserver nunca montava os trilhos seguintes.
 * Metade do "não dá pra descer" na TV do dono.
 *
 * Correção por CAPACIDADE (nada de sniff de engine):
 *   - client box = VIEWPORT, sempre de `documentElement.clientHeight/Width`
 *     (== a viewport real em qualquer engine, inclusive quando o scroller é o
 *     `<body>`);
 *   - `scrollHeight/Width` = os do scroller real (o conteúdo rolável);
 *   - escreve o scroll em `documentElement` E `body` (um é o scroller e move; o
 *     outro clampa em 0, no-op inócuo) — dispensa adivinhar QUAL é o scroller,
 *     que diverge entre Chr38, Chr53 e Chrome moderno. Lê pelo `pageYOffset`
 *     canônico (com fallback pros dois `scrollTop`).
 */
function rootScrollBox(doc: Document): ScrollBoxLike {
  const de = doc.documentElement
  const body: HTMLElement | null = doc.body
  const view = doc.defaultView
  const scrollerH = () => Math.max(de.scrollHeight, body ? body.scrollHeight : 0)
  const scrollerW = () => Math.max(de.scrollWidth, body ? body.scrollWidth : 0)
  return {
    getBoundingClientRect: () => ({ top: 0, bottom: de.clientHeight, left: 0, right: de.clientWidth }),
    clientTop: 0,
    clientLeft: 0,
    clientHeight: de.clientHeight,
    clientWidth: de.clientWidth,
    get scrollHeight() {
      return scrollerH()
    },
    get scrollWidth() {
      return scrollerW()
    },
    get scrollTop() {
      return view ? view.pageYOffset : de.scrollTop || (body ? body.scrollTop : 0)
    },
    set scrollTop(v: number) {
      de.scrollTop = v
      if (body) body.scrollTop = v
    },
    get scrollLeft() {
      return view ? view.pageXOffset : de.scrollLeft || (body ? body.scrollLeft : 0)
    },
    set scrollLeft(v: number) {
      de.scrollLeft = v
      if (body) body.scrollLeft = v
    },
  }
}

/** A página rola em algum eixo? Compara o conteúdo rolável contra a VIEWPORT
 *  (documentElement.clientX), NÃO contra o clientHeight do scroller — que no
 *  Chr53 (scroller = body) é ~a altura do conteúdo e daria "não rola" sempre. */
function rootOverflows(box: ScrollBoxLike): boolean {
  return box.scrollHeight > box.clientHeight || box.scrollWidth > box.clientWidth
}

/**
 * Cadeia de containers de scroll do elemento, do MAIS INTERNO pro mais externo,
 * terminando no root scroller (a página) quando ele rola. Mesma ordem em que o
 * `scrollIntoView` nativo resolve — o container interno se ajusta primeiro e só
 * então o externo, e por isso o rect do elemento é RE-MEDIDO a cada passo.
 *
 * O root vem como um `ScrollBoxLike` (não o Element cru): ver `rootScrollBox`, que
 * corrige a caixa da viewport e o alvo da escrita no Chr53.
 */
function findScrollContainers(el: Element): { box: ScrollBoxLike; isRoot: boolean }[] {
  const out: { box: ScrollBoxLike; isRoot: boolean }[] = []
  const doc = el.ownerDocument
  if (!doc) return out

  // `||` e não `??`: `scrollingElement` pode ser null (quirks mode), e não existe
  // antes do Chr44 — mesmo guard que o resto do app usa.
  const rootEl: Element | null = doc.scrollingElement || doc.documentElement

  let node: Element | null = el.parentElement
  while (node && node !== rootEl && node !== doc.body && node !== doc.documentElement) {
    if (isScrollContainer(node)) out.push({ box: node as unknown as ScrollBoxLike, isRoot: false })
    node = node.parentElement
  }

  const root = rootScrollBox(doc)
  if (rootOverflows(root)) out.push({ box: root, isRoot: true })
  return out
}

/**
 * `scrollIntoView` com options object só existe no Chr61+. No Chr <61 a única
 * assinatura é `scrollIntoView(alignToTop: boolean)` — passar um objeto o coage
 * a `true`, ou seja, SEMPRE alinha ao topo e `block`/`inline` viram pó.
 *
 * Consequência real na TV (era o comportamento do `Focusable` antes desta
 * parede): a cada seta do D-pad, `{block:'nearest'}` virava "alinha ao topo" e a
 * lista/rail PULAVA pro começo em vez de rolar um item. Navegação aos solavancos.
 *
 * Aqui:
 *   - Chr61+ → `scrollIntoView(opts)` nativo (zero mudança de comportamento na
 *     web e nos devices modernos; `behavior:'smooth'` continua valendo).
 *   - Chr <61 → NÓS fazemos o scroll, medindo e escrevendo `scrollTop`/
 *     `scrollLeft` (que funcionam em qualquer engine). `nearest` vira o mínimo
 *     deslocamento de verdade — e ZERO quando o item já está visível.
 *     `behavior:'smooth'` é ignorado (scroll instantâneo): no piso Chr38 o alvo
 *     é ACERTAR a posição, não animá-la.
 *   - Chr <61 sem nada pra medir (elemento fora do documento, sem container de
 *     scroll, DOM exótico) → último recurso: o boolean legacy, que ao menos traz
 *     o alvo à viewport sem quebrar.
 *
 * Defaults iguais aos do spec (`block: 'start'`, `inline: 'nearest'`), pra que o
 * caminho legacy e o nativo decidam a MESMA coisa.
 */
export function scrollIntoViewCompat(
  el: Element | null | undefined,
  opts?: ScrollIntoViewOptions,
): void {
  if (!el) return
  // `scrollIntoView` existe em todo engine real, mas o guard mantém o helper
  // seguro em ambiente sem DOM completo (o `Focusable` fazia esse check antes de
  // chamar; agora ele mora aqui, no dono do compat, e vale pra TODO call site).
  const hasScrollIntoView = typeof el.scrollIntoView === 'function'

  if (NATIVE_SCROLL_OPTIONS_SUPPORTED && hasScrollIntoView) {
    el.scrollIntoView(opts)
    return
  }
  if (scrollIntoViewLegacy(el, opts)) return
  if (hasScrollIntoView) el.scrollIntoView(legacyAlignToTop(opts?.block))
}

/**
 * Caminho legacy do `scrollIntoViewCompat`. Devolve `true` quando conseguiu
 * RESOLVER o scroll por medição — inclusive quando a decisão correta foi NÃO
 * rolar (item já visível). Devolve `false` só quando não há o que medir, pra que
 * o chamador caia no fallback boolean.
 */
function scrollIntoViewLegacy(el: Element, opts?: ScrollIntoViewOptions): boolean {
  if (typeof el.getBoundingClientRect !== 'function') return false
  const containers = findScrollContainers(el)
  if (containers.length === 0) return false

  const block = opts?.block ?? 'start'
  const inline = opts?.inline ?? 'nearest'

  // Loop por índice, não `for...of`: este código roda a CADA seta do D-pad e, no
  // bundle legacy (alvo Chr38), `for...of` vira maquinaria de Symbol.iterator via
  // core-js — alocação por foco, na TV mais fraca do parque. Aqui é array puro.
  for (let i = 0; i < containers.length; i++) {
    const c = containers[i]
    if (!c) continue
    scrollElementIntoScrollBox(c.box, el, block, inline, c.isRoot)
  }
  return true
}

/**
 * Mapeia `block` do options pro boolean `alignToTop` do `scrollIntoView` legacy:
 * 'end' → base (false); 'start'/'center'/'nearest' → topo (true, garante o alvo
 * visível). É o ÚLTIMO recurso do caminho legacy (quando não há container de
 * scroll pra medir) — não o caminho normal, justamente porque "alinha ao topo" é
 * o comportamento ruim que esta parede veio matar. Exportado pra teste
 * determinístico do fallback.
 */
export function legacyAlignToTop(block: ScrollLogicalPosition | undefined): boolean {
  return block !== 'end'
}
